'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import { useState } from 'react';
import { FORUM_IMAGE_ACCEPT, MAX_FORUM_IMAGES, uploadForumImage } from '@/platform/forum/forumUploads';
import './forum-actions.css';

export function useDiscussionComposeMedia(opts?: { tourMode?: boolean }) {
  const tourMode = opts?.tourMode ?? false;
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  function resetMedia() {
    setYoutubeUrl('');
    setImagePaths([]);
    setImagePreviews([]);
    setUploadError('');
  }

  function clearImages() {
    setImagePaths([]);
    setImagePreviews([]);
  }

  function clearYoutube() {
    setYoutubeUrl('');
  }

  async function addPhotos(files: FileList | null) {
    if (!files?.length) return;
    const remaining = MAX_FORUM_IMAGES - imagePaths.length;
    if (remaining <= 0) {
      setUploadError(`You can attach up to ${MAX_FORUM_IMAGES} photos.`);
      return;
    }
    setUploadError('');
    setUploading(true);
    try {
      const picked = Array.from(files).slice(0, remaining);
      for (const file of picked) {
        if (tourMode) {
          setImagePaths((prev) => [...prev, `forum/tour/${file.name}`]);
          setImagePreviews((prev) => [...prev, URL.createObjectURL(file)]);
          continue;
        }
        const uploaded = await uploadForumImage(file);
        setImagePaths((prev) => [...prev, uploaded.objectPath]);
        setImagePreviews((prev) => [...prev, uploaded.publicUrl]);
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return {
    youtubeUrl,
    setYoutubeUrl,
    imagePaths,
    imagePreviews,
    uploading,
    uploadError,
    setUploadError,
    addPhotos,
    resetMedia,
    clearImages,
    clearYoutube,
  };
}

type FieldsProps = {
  youtubeUrl: string;
  onYoutubeUrl: (value: string) => void;
  imagePreviews: string[];
  uploading: boolean;
  imageCount: number;
  onAddFiles: (files: FileList | null) => void;
  youtubeInputId?: string;
  /** Share-box type: video = YouTube only; photo = uploads only; post = none. */
  mode?: ForumComposeMediaMode;
};

export type ForumComposeMediaMode = 'post' | 'video' | 'photo';

export function ForumComposeMediaFields({
  youtubeUrl,
  onYoutubeUrl,
  imagePreviews,
  uploading,
  imageCount,
  onAddFiles,
  youtubeInputId = 'forum-youtube-url',
  mode = 'post',
}: FieldsProps) {
  const disabled = uploading || imageCount >= MAX_FORUM_IMAGES;
  const showVideo = mode === 'video';
  const showPhotos = mode === 'photo';
  return (
    <>
      {showVideo ? (
        <>
          <label className="forum-compose-media__label" htmlFor={youtubeInputId}>
            YouTube video URL
          </label>
          <input
            id={youtubeInputId}
            className="feed-card__compose-input"
            value={youtubeUrl}
            onChange={(e) => onYoutubeUrl(e.target.value)}
            placeholder="https://youtu.be/…"
            autoFocus
            data-testid="feed-compose-youtube"
          />
          <p className="text-muted forum-compose-media__hint">
            Paste a YouTube link — native video files are not accepted.
          </p>
        </>
      ) : null}
      {showPhotos ? (
        <>
          <div className="forum-compose-media">
            <label className="btn btn-secondary">
              Add photos
              <input
                className="forum-compose-media__file-input"
                type="file"
                accept={FORUM_IMAGE_ACCEPT}
                multiple
                disabled={disabled}
                onChange={(e) => {
                  onAddFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="btn btn-secondary">
              Take photo
              <input
                className="forum-compose-media__capture-input"
                type="file"
                accept={FORUM_IMAGE_ACCEPT}
                capture="environment"
                disabled={disabled}
                onChange={(e) => {
                  onAddFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          {imagePreviews.length > 0 ? (
            <div className="forum-compose-media__thumbs">
              {imagePreviews.map((src) => (
                <img key={src} className="forum-compose-media__thumb" src={src} alt={contentImageAlt(null, 'Selected photo')} />
              ))}
            </div>
          ) : null}
          <p className="text-muted forum-compose-media__hint">Photos upload here.</p>
        </>
      ) : null}
    </>
  );
}
